import { getMeta, setMeta, type OfficeDb } from '../lib/db.js'
import { apiBase } from '../lib/config.js'

/**
 * Sign-in, and the identity every write is attributed to (docs/06 §"The four
 * accounts").
 *
 * Three rules shape this file, and each of them exists because the obvious
 * alternative breaks the wall tablet:
 *
 *  1. **The session lives in Dexie, not `localStorage`.** The iPad runs the app
 *     inside a WKWebView, and a WebView's web storage is cleared on a different
 *     schedule from its IndexedDB — "Clear website data", a low-storage purge and
 *     an app update do not treat the two the same. The mirror already survives
 *     in IndexedDB; the token that lets it sync must survive with it, or the
 *     wall silently stops syncing until somebody notices and types a password.
 *
 *  2. **Restoring never touches the network.** A signed-in session must come
 *     back with the internet down. Tokens are long-lived by design and
 *     revocation is explicit (the server flips `revoked_at`), so there is
 *     nothing to refresh and nothing to wait for at boot.
 *
 *  3. **Signing out clears locally even when the server cannot be reached.**
 *     `/auth/logout` is best effort. Refusing to sign out because the wifi is
 *     down would leave someone's session on a shared device, which is the exact
 *     failure sign-out exists to prevent.
 *
 * Nothing here reads the ambient clock: sessions carry no client-side expiry,
 * because a device whose clock cannot be trusted (docs/02 §4) cannot be trusted
 * to decide that a token has expired either. The server decides.
 */

/** Where the session is kept in Dexie's `meta` table. */
export const SESSION_KEY = 'auth.session'

export const DEFAULT_AUTH_TIMEOUT_MS = 20_000

export interface AuthAccount {
  readonly id: string
  readonly email: string
  /**
   * The person this account acts as, or `null` for the iPad. A null personId is
   * NOT a missing value to be filled in — it is the wall tablet, which must ask
   * who is standing in front of it rather than inventing somebody.
   */
  readonly personId: string | null
  readonly isAdmin: boolean
  readonly isDevice: boolean
}

export interface AuthSession {
  readonly token: string
  readonly account: AuthAccount
}

export type SignInFailure =
  | 'credentials'   // wrong email or password — the only one that is the user's fault
  | 'network'       // never reached the server
  | 'timeout'       // reached it, no answer inside the deadline
  | 'server'        // reached it, it refused for some other reason
  | 'malformed'     // answered with something we refuse to trust

/**
 * A sign-in failure with a message meant to be READ by the person at the
 * keyboard. The form renders `message` verbatim, so it says what happened and
 * what to do about it rather than leaking a status code at somebody.
 */
export class SignInError extends Error {
  readonly kind: SignInFailure
  readonly status: number | null

  constructor(kind: SignInFailure, message: string, status: number | null = null, options?: ErrorOptions) {
    super(message, options)
    this.name = 'SignInError'
    this.kind = kind
    this.status = status
  }
}

export interface AuthEndpoint {
  /** Defaults to `apiBase()` — config.ts is the single source of truth. */
  baseUrl?: string
  /** Injected in tests; defaults to the runtime's own `fetch`. */
  fetchImpl?: typeof fetch
  timeoutMs?: number
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** The server validates `deviceId` as a uuid; anything else is left off rather than 400'd. */
export const isUuid = (v: unknown): v is string => typeof v === 'string' && UUID.test(v)

/**
 * Parse an account off the wire.
 *
 * Strict on purpose. A half-read account gets STORED and then decides who every
 * write is attributed to, so a shape we do not fully recognise is refused
 * outright rather than patched up with defaults.
 */
export function parseAccount(v: unknown): AuthAccount | null {
  if (!isRecord(v)) return null
  const { id, email, personId, isAdmin, isDevice } = v
  if (typeof id !== 'string' || id === '') return null
  if (typeof email !== 'string' || email === '') return null
  if (personId !== null && typeof personId !== 'string') return null
  if (typeof personId === 'string' && personId === '') return null
  if (typeof isAdmin !== 'boolean' || typeof isDevice !== 'boolean') return null
  return { id, email, personId, isAdmin, isDevice }
}

export function parseSession(v: unknown): AuthSession | null {
  if (!isRecord(v)) return null
  if (typeof v.token !== 'string' || v.token === '') return null
  const account = parseAccount(v.account)
  return account ? { token: v.token, account } : null
}

/**
 * One request, headers AND body inside a single deadline.
 *
 * The same reasoning as `httpTransport`: a captive portal that answers the
 * headers and then stalls the stream is indistinguishable from a dead socket
 * unless the timer spans the body read too.
 */
async function post(
  path: string,
  body: unknown,
  token: string | null,
  opts: AuthEndpoint,
): Promise<{ status: number; text: string }> {
  const doFetch = opts.fetchImpl ?? (globalThis as { fetch?: typeof fetch }).fetch
  if (!doFetch) throw new SignInError('network', 'This device has no way to reach the network.')

  const base = (opts.baseUrl ?? apiBase()).replace(/\/+$/, '')
  const ctl = new AbortController()
  let timedOut = false
  const timer = setTimeout(() => { timedOut = true; ctl.abort() }, opts.timeoutMs ?? DEFAULT_AUTH_TIMEOUT_MS)

  try {
    const res = await doFetch(`${base}${path}`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        ...(token !== null ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal: ctl.signal,
    })
    return { status: res.status, text: await res.text() }
  } catch (e) {
    if (e instanceof SignInError) throw e
    if (timedOut) {
      throw new SignInError('timeout', 'The office server did not answer. Try again.', null, { cause: e })
    }
    throw new SignInError('network', 'Cannot reach the office server. Check the connection.', null, { cause: e })
  } finally {
    clearTimeout(timer)
  }
}

export interface SignInInput extends AuthEndpoint {
  /** May be null: a blocked IndexedDB must not stop somebody signing in. */
  db: OfficeDb | null
  email: string
  password: string
  /** This device's id, so the server can tell one signed-in device from another. */
  deviceId?: string | null
}

/**
 * `POST /auth/login` → `{ token, account }`, stored in Dexie before it returns.
 *
 * The account comes from the SERVER, never from the email that was typed. Which
 * person a token acts as, and whether it may change settings, are the server's
 * to decide — a client that inferred `isAdmin` from the address would hand admin
 * to anybody who could type `bayo@sotech.com`.
 */
export async function signIn(input: SignInInput): Promise<AuthSession> {
  const email = input.email.trim()
  const { password } = input
  if (email === '' || password === '') {
    throw new SignInError('credentials', 'Enter your email and password.')
  }

  const body: Record<string, unknown> = { email, password }
  if (isUuid(input.deviceId)) body.deviceId = input.deviceId

  const { status, text } = await post('/auth/login', body, null, input)

  // One message for both "no such account" and "wrong password", matching the
  // server: telling them apart would enumerate who has an account here.
  if (status === 401) throw new SignInError('credentials', 'Email or password is wrong.', 401)
  if (status < 200 || status >= 300) {
    throw new SignInError('server', `The office server refused the sign-in (${status}).`, status)
  }

  let parsed: unknown
  try { parsed = JSON.parse(text) } catch (e) {
    throw new SignInError('malformed', 'The server sent an answer this app could not read.', status, { cause: e })
  }
  const session = parseSession(parsed)
  if (!session) {
    throw new SignInError('malformed', 'The server sent an answer this app could not read.', status)
  }

  await saveSession(input.db, session)
  return session
}

export interface SignOutInput extends AuthEndpoint {
  db: OfficeDb | null
  /** The token to revoke server-side. Null still clears locally. */
  token: string | null
}

/**
 * Forget locally FIRST, then revoke server-side. Best effort on the server, and
 * signed out either way.
 *
 * The order is the whole point. `/auth/logout` gets up to twenty seconds, and
 * the failure that costs those seconds is office wifi behind a captive portal
 * that accepts the connection and then never answers. Clearing afterwards means
 * that for those twenty seconds the token is still sitting in IndexedDB — and a
 * WebView killed in that window (the iPad after a power cut, a phone the OS
 * reclaims, anybody who force-quits rather than waiting on a spinner) comes back
 * up and RESTORES the session somebody had just signed out of, on a device they
 * have already handed over.
 *
 * The token is kept in a local, so the revoke still names it after the store has
 * forgotten it.
 */
export async function signOut(input: SignOutInput): Promise<void> {
  const token = input.token
  await clearSession(input.db)
  if (token !== null && token !== '') {
    try { await post('/auth/logout', {}, token, input) } catch { /* best effort */ }
  }
}

/**
 * The session as it was left. No network, ever.
 *
 * A row we cannot parse is deleted rather than kept: it would otherwise fail the
 * same way on every boot, forever, with nothing on screen saying why.
 */
export async function restore(db: OfficeDb | null): Promise<AuthSession | null> {
  if (!db) return null
  try {
    const raw = await getMeta<unknown>(db, SESSION_KEY, null)
    if (raw === null || raw === undefined) return null
    const session = parseSession(raw)
    if (!session) { await clearSession(db); return null }
    return session
  } catch {
    // An unavailable IndexedDB means "not signed in", never a crash at boot.
    return null
  }
}

/** Storing is best effort: a session that cannot be persisted still works for this run. */
export async function saveSession(db: OfficeDb | null, session: AuthSession): Promise<void> {
  if (!db) return
  try { await setMeta(db, SESSION_KEY, session) } catch { /* in-memory session only */ }
}

/**
 * Should a 401 cost this device its stored session?
 *
 * Extracted and exported because it is the decision that was WRONG, and because
 * the race that made it wrong cannot be reproduced in jsdom — fake-indexeddb
 * resolves fast enough that the boot effect always wins, so a rendered test
 * passed with the bug fully present. Pinning the rule itself is what actually
 * holds it.
 *
 * Two rules, and the first one is the fix:
 *
 *  1. **A request that carried NO token proves nothing.** Its 401 means only
 *     "you did not ask properly". The sync loop can fire before the stored
 *     session has been read back out of IndexedDB, and that pull goes out
 *     anonymous; treating its refusal as a verdict on the stored token deleted
 *     valid sessions on every boot. The wall tablet suffered worst — nobody is
 *     standing at it to type the password back in.
 *
 *  2. **Otherwise, only the token actually in use.** A 401 can outlive the
 *     request that earned it: one sent before somebody signed in can land after
 *     they did, and signing that fresh session straight back out would leave the
 *     form on screen with no explanation.
 *
 * What it must NOT do is refuse to forget a genuinely refused token. That turns
 * a revoked session into one the device retries forever — head-of-line blocking
 * wearing an authentication hat.
 */
export function shouldForgetSession(
  rejectedToken: string | null,
  currentToken: string | null,
): boolean {
  if (rejectedToken === null) return false
  return rejectedToken === currentToken
}

export async function clearSession(db: OfficeDb | null): Promise<void> {
  if (!db) return
  try { await db.meta.delete(SESSION_KEY) } catch { /* nothing left to clear */ }
}
